import { useState, useRef, useEffect, useCallback } from 'react';
import { simulateMessage, resetSimulatorSession, SimulatorOutboundMessage } from '../api/client';

interface ChatMessage {
  id: string;
  type: 'user' | 'agent';
  content: string;
  timestamp: Date;
  rendered?: SimulatorOutboundMessage['rendered'];
  card_data?: any;
}

export default function Simulator() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [phone, setPhone] = useState('+972501234567');
  const [senderName] = useState('Simulator User');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevPhoneRef = useRef(phone);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Clear chat and reset backend session when phone changes
  const resetSession = useCallback((newPhone: string) => {
    setMessages([]);
    resetSimulatorSession(newPhone).catch(() => {
      // Best-effort reset — session will be re-created on next message
    });
  }, []);

  useEffect(() => {
    if (prevPhoneRef.current !== phone && messages.length > 0) {
      resetSession(phone);
    }
    prevPhoneRef.current = phone;
  }, [phone, messages.length, resetSession]);

  const handleSendMessage = async (text: string, isCardReply = false, callbackData?: string) => {
    if (!text.trim() && !isCardReply) return;

    const userMsg: ChatMessage = {
      id: Math.random().toString(36).substr(2, 9),
      type: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await simulateMessage({
        phone,
        text,
        is_card_reply: isCardReply,
        callback_data: callbackData,
        sender_name: senderName,
      });

      const agentMessages: ChatMessage[] = response.outbound.map((msg) => ({
        id: Math.random().toString(36).substr(2, 9),
        type: 'agent',
        content: msg.type === 'text' ? (msg.text || '') : (msg.rendered?.body || ''),
        timestamp: new Date(),
        rendered: msg.rendered,
        card_data: msg.card_data,
      }));

      setMessages((prev) => [...prev, ...agentMessages]);
    } catch (error) {
      console.error('Simulator error:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: 'error',
          type: 'agent',
          content: 'Error: Failed to get response from agent.',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const renderCardContent = (msg: ChatMessage) => {
    if (!msg.rendered || !msg.rendered.reply_markup?.twilio_content) return null;

    const variables = msg.rendered.reply_markup.twilio_content.content_variables || {};
    
    // Quick Replies: Usually indices 2, 3, 4
    const quickReplies = [];
    for (let i = 2; i <= 4; i++) {
      const btn = variables[i.toString()];
      if (btn) quickReplies.push(btn);
    }

    // List Picker: Usually starts from index 3 (title, desc)
    const listItems = [];
    let idx = 3;
    while (variables[idx.toString()] && variables[(idx + 1).toString()]) {
      listItems.push({
        title: variables[idx.toString()],
        description: variables[(idx + 1).toString()],
      });
      idx += 2;
    }

    const isList = listItems.length > quickReplies.length;

    return (
      <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
        {isList ? (
          <>
            <div className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">
              {variables['2'] || 'Select an option'}
            </div>
            <div className="grid gap-2">
              {listItems.map((item, i) => (
                <button
                  key={i}
                  onClick={() => handleSendMessage(item.title, true, item.title)}
                  className="w-full text-left p-3 rounded-lg border border-primary-100 hover:bg-primary-50 transition-colors group"
                >
                  <div className="font-medium text-sm text-primary-700 group-hover:text-primary-800">
                    {item.title}
                  </div>
                  {item.description && (
                    <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                  )}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="flex flex-wrap gap-2">
            {quickReplies.map((btn, i) => (
              <button
                key={i}
                onClick={() => handleSendMessage(btn, true, btn)}
                className="px-4 py-2 bg-white border border-primary-200 rounded-full text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors shadow-sm"
              >
                {btn}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm z-10">
        <div className="flex items-center gap-3">
          <div className="bg-primary-600 p-2 rounded-lg">
            <span className="text-xl">📱</span>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">WhatsApp Simulator</h1>
            <p className="text-xs text-gray-500">Test agent conversation logic locally</p>
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <div className="flex flex-col">
            <label className="text-[10px] uppercase font-bold text-gray-400 mb-0.5">Simulated Phone</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>
          <button
            onClick={() => setMessages([])}
            className="text-xs font-medium text-red-600 hover:text-red-700 px-3 py-1 rounded hover:bg-red-50 transition-colors"
          >
            Clear History
          </button>
        </div>
      </header>

      {/* Chat Area */}
      <main className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-20">
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 inline-block max-w-md">
                <span className="text-5xl block mb-4">💬</span>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Start a Conversation</h2>
                <p className="text-gray-500 text-sm mb-6">
                  Type a message below to see how the agent responds. You can test templates, commands, and automation flows.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => handleSendMessage('Hello')}
                    className="text-xs p-2 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 text-gray-600 transition-colors"
                  >
                    Send "Hello"
                  </button>
                  <button 
                    onClick={() => handleSendMessage('Menu')}
                    className="text-xs p-2 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 text-gray-600 transition-colors"
                  >
                    Send "Menu"
                  </button>
                </div>
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                  msg.type === 'user'
                    ? 'bg-primary-600 text-white rounded-tr-none'
                    : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none'
                }`}
              >
                {msg.type === 'agent' && (
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary-500">Dada Agent</span>
                    {msg.rendered && (
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">Template</span>
                    )}
                  </div>
                )}
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {msg.content}
                </div>
                {msg.type === 'agent' && renderCardContent(msg)}
                <div className={`text-[10px] mt-1.5 opacity-60 ${msg.type === 'user' ? 'text-right' : 'text-left'}`}>
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input Area */}
      <footer className="bg-white border-t border-gray-200 p-4 shadow-lg z-10">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage(inputValue);
          }}
          className="max-w-3xl mx-auto flex gap-3"
        >
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all shadow-inner"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !inputValue.trim()}
            className="bg-primary-600 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md transform active:scale-95"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 rotate-90" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </button>
        </form>
        <p className="text-center text-[10px] text-gray-400 mt-2">
          Messages are processed synchronously. No real WhatsApp messages will be sent.
        </p>
      </footer>
    </div>
  );
}
